import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tldraw, Editor, TLEditorSnapshot } from 'tldraw';
import 'tldraw/tldraw.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getWhiteboard, saveWhiteboard, clearWhiteboard } from '../services/whiteboardService';
import { getProjectById } from '../services/projectService';
import { Layout } from '../components/Layout';

/**
 * Save status indicator component
 */
const SaveStatus: React.FC<{
  status: 'saved' | 'saving' | 'unsaved' | 'error';
  lastSaved: Date | null;
}> = ({ status, lastSaved }) => {
  const statusConfig = {
    saved: { color: 'text-green-600 dark:text-green-400', text: 'Saved' },
    saving: { color: 'text-blue-600 dark:text-blue-400', text: 'Saving...' },
    unsaved: { color: 'text-yellow-600 dark:text-yellow-400', text: 'Unsaved changes' },
    error: { color: 'text-red-600 dark:text-red-400', text: 'Save failed' }
  };

  const config = statusConfig[status];

  return (
    <div className={`flex items-center gap-2 text-sm ${config.color}`}>
      {status === 'saving' ? (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : status === 'saved' ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : status === 'unsaved' ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )}
      <span>{config.text}</span>
      {lastSaved && status === 'saved' && (
        <span className="text-gray-500 dark:text-gray-400 text-xs">
          ({lastSaved.toLocaleTimeString()})
        </span>
      )}
    </div>
  );
};

/**
 * Error boundary component for tldraw
 */
class WhiteboardErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onReset: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Whiteboard error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
          <div className="text-center p-8">
            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              Whiteboard Error
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Something went wrong with the whiteboard.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                this.props.onReset();
              }}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
            >
              Reload Whiteboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ProjectWhiteboard Component
 *
 * Provides an infinite canvas whiteboard for project collaboration using tldraw.
 */
const ProjectWhiteboard: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [initialSnapshot, setInitialSnapshot] = useState<TLEditorSnapshot | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved' | 'error'>('saved');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [canClear, setCanClear] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [key, setKey] = useState(0); // Used to force re-render tldraw

  const editorRef = useRef<Editor | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const handleStoreChangeRef = useRef<(() => void) | null>(null);

  // Determine if user can clear whiteboard (project manager+ or admin)
  useEffect(() => {
    if (user) {
      const canClearWhiteboard =
        user.role === 'platform_admin' ||
        user.role === 'company_admin' ||
        user.role === 'project_manager' ||
        user.role === 'project_manager_lite';
      setCanClear(canClearWhiteboard);
    }
  }, [user]);

  // Load whiteboard data
  useEffect(() => {
    const loadData = async () => {
      if (!projectId || !token) return;

      try {
        setLoading(true);
        setError(null);

        // Load project info and whiteboard in parallel
        const [projectResponse, whiteboardResponse] = await Promise.all([
          getProjectById(projectId, token),
          getWhiteboard(projectId, token)
        ]);

        setProjectName(projectResponse.project.name);

        // Parse canvas data if available
        if (whiteboardResponse.canvas_data) {
          try {
            const snapshot = JSON.parse(whiteboardResponse.canvas_data);
            setInitialSnapshot(snapshot);
            lastSaveRef.current = whiteboardResponse.canvas_data;
          } catch (e) {
            console.error('Failed to parse canvas data:', e);
          }
        }

        if (whiteboardResponse.updated_at) {
          setLastSaved(new Date(whiteboardResponse.updated_at));
        }
      } catch (err: any) {
        console.error('Failed to load whiteboard:', err);
        setError(err.message || 'Failed to load whiteboard');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token]);

  // Handle save with debouncing
  const performSave = useCallback(async () => {
    console.log('[Whiteboard] performSave called', { projectId, hasToken: !!token, hasEditor: !!editorRef.current });
    if (!projectId || !token || !editorRef.current) {
      console.log('[Whiteboard] performSave early return - missing required data');
      return;
    }

    try {
      // Get snapshot from editor
      const snapshot = editorRef.current.getSnapshot();
      const data = JSON.stringify(snapshot);
      console.log('[Whiteboard] Snapshot size:', data.length, 'bytes');

      // Only save if data has changed
      if (data === lastSaveRef.current) {
        console.log('[Whiteboard] Data unchanged, skipping save');
        return;
      }

      console.log('[Whiteboard] Saving whiteboard data...');
      setSaveStatus('saving');
      const result = await saveWhiteboard(projectId, data, token);
      console.log('[Whiteboard] Save successful, version:', result.version);
      lastSaveRef.current = data;
      setSaveStatus('saved');
      setLastSaved(new Date(result.updated_at));
    } catch (err: any) {
      console.error('[Whiteboard] Failed to save whiteboard:', err);
      setSaveStatus('error');
    }
  }, [projectId, token]);

  // Handle store change with debouncing
  const handleStoreChange = useCallback(() => {
    console.log('[Whiteboard] Store change detected, scheduling save...');
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setSaveStatus('unsaved');

    // Debounce save for 3 seconds after last change
    debounceTimerRef.current = setTimeout(() => {
      console.log('[Whiteboard] Debounce timer fired, performing save...');
      performSave();
    }, 3000);
  }, [performSave]);

  // Keep ref updated with latest callback
  useEffect(() => {
    handleStoreChangeRef.current = handleStoreChange;
  }, [handleStoreChange]);

  // Handle editor mount
  const handleMount = useCallback((editor: Editor) => {
    console.log('[Whiteboard] Editor mounted');
    editorRef.current = editor;

    // Clean up previous listener if any
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Load initial snapshot if available
    if (initialSnapshot) {
      try {
        console.log('[Whiteboard] Loading initial snapshot...');
        editor.loadSnapshot(initialSnapshot);
        console.log('[Whiteboard] Initial snapshot loaded successfully');
      } catch (e) {
        console.error('[Whiteboard] Failed to load initial snapshot:', e);
      }
    }

    // Subscribe to store changes - use source 'user' to only catch user actions
    // and scope 'document' to filter for document changes only (not session/presence)
    console.log('[Whiteboard] Setting up store listener...');
    unsubscribeRef.current = editor.store.listen((entry) => {
      console.log('[Whiteboard] Store change event fired', {
        source: entry.source,
        changeCount: Object.keys(entry.changes.added).length +
                     Object.keys(entry.changes.updated).length +
                     Object.keys(entry.changes.removed).length
      });
      // Use ref to always call latest callback version
      if (handleStoreChangeRef.current) {
        handleStoreChangeRef.current();
      }
    }, { source: 'user', scope: 'document' });
    console.log('[Whiteboard] Store listener registered');
  }, [initialSnapshot]);

  // Handle clear
  const handleClear = async () => {
    if (!projectId || !token) return;

    try {
      await clearWhiteboard(projectId, token);
      setInitialSnapshot(null);
      lastSaveRef.current = null;
      setKey(prev => prev + 1); // Force tldraw to re-render
      setShowClearConfirm(false);
      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (err: any) {
      console.error('Failed to clear whiteboard:', err);
      setError(err.message || 'Failed to clear whiteboard');
    }
  };

  // Handle error boundary reset
  const handleReset = () => {
    setKey(prev => prev + 1);
  };

  // Save on unmount if there are pending changes
  useEffect(() => {
    return () => {
      console.log('[Whiteboard] Component unmounting, cleaning up...');
      // Cancel any pending debounced save
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Perform final save synchronously if we have an editor
      if (editorRef.current && projectId && token) {
        try {
          const snapshot = editorRef.current.getSnapshot();
          const data = JSON.stringify(snapshot);
          if (data !== lastSaveRef.current) {
            console.log('[Whiteboard] Saving on unmount...');
            // Use synchronous XMLHttpRequest for unmount save
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', `${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/projects/${projectId}/whiteboard`, false);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(JSON.stringify({ canvas_data: data }));
            console.log('[Whiteboard] Unmount save complete, status:', xhr.status);
          }
        } catch (e) {
          console.error('[Whiteboard] Failed to save on unmount:', e);
        }
      }
      // Unsubscribe from store
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [projectId, token]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading whiteboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              Error Loading Whiteboard
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">{error}</p>
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
            >
              Back to Project
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="text-sm text-blue-100 hover:text-white mb-1 inline-flex items-center transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Project
              </button>
              <h1 className="text-2xl font-bold text-white">
                {projectName} - Whiteboard
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full px-4 py-2 shadow-sm">
                <SaveStatus status={saveStatus} lastSaved={lastSaved} />
              </div>
              {canClear && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/30 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Whiteboard Canvas */}
        <div className="flex-1 relative">
          <WhiteboardErrorBoundary onReset={handleReset}>
            <Tldraw
              key={key}
              onMount={handleMount}
              inferDarkMode={theme === 'dark'}
            />
          </WhiteboardErrorBoundary>
        </div>

        {/* Clear Confirmation Modal */}
        {showClearConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Clear Whiteboard?
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                This will permanently delete all content on the whiteboard. This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClear}
                  className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                >
                  Clear Whiteboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProjectWhiteboard;
